import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { EmptyChart, hasChartData } from "../../components/EmptyChart";
import CustomTable from "../../components/CustomTable";
import PieChart from "../../components/PieChart";
import Button from "../../components/Button";

const tableColumns = [
  { key: "index", title: "STT", minWidth: "56px" },
  {
    key: "serviceName",
    title: "Dịch vụ",
    minWidth: "260px",
    cellClassName: "!whitespace-normal !overflow-visible break-words",
  },
  {
    key: "total",
    title: "Số lượng",
    minWidth: "120px",
    align: "right",
    render: (value) => (
      <span className="font-sf-semibold text-gray-900">
        {Number(value || 0).toLocaleString("vi-VN")}
      </span>
    ),
  },
  {
    key: "percent",
    title: "Tỷ lệ",
    minWidth: "120px",
    align: "right",
    render: (value) => (
      <span className="text-sm font-medium text-[#0457C2]">
        {Number(value || 0).toLocaleString("vi-VN")}%
      </span>
    ),
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.22 } },
};

const AssetReportSummary = ({
  chart,
  error,
  loading,
  onRetry,
  rows,
  summary,
  variant = "full",
}) => {
  const hasData = hasChartData(chart);
  const formattedTotalAssets = useMemo(
    () => Number(summary?.totalAssets || 0).toLocaleString("vi-VN"),
    [summary?.totalAssets],
  );

  const chartContent = (
    <div className="flex h-full min-h-[360px] items-center justify-center rounded-[12px] border border-[#E3E3E3] bg-white p-4 sm:p-6">
      {loading ? (
        <ChartSkeleton />
      ) : error ? (
        <RetryState error={error} onRetry={onRetry} />
      ) : hasData ? (
        <div className="w-full max-w-[360px]">
          <PieChart
            data={chart}
            size={260}
            centerSubLabel="Tổng tài sản"
            legendPosition="bottom"
          />
        </div>
      ) : (
        <EmptyChart title="Chưa có dữ liệu biểu đồ" />
      )}
    </div>
  );

  if (variant === "chart") {
    return (
      <motion.section
        variants={cardVariants}
        initial="hidden"
        animate="show"
        className="h-full min-h-0"
      >
        {chartContent}
      </motion.section>
    );
  }

  return (
    <motion.section
      variants={cardVariants}
      initial="hidden"
      animate="show"
      className="rounded-[16px] border border-[#D2D5DA] bg-white p-4 shadow-sm sm:p-6"
    >
      <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex flex-wrap gap-3">
          <SummaryMetric label="Tổng tài sản" value={formattedTotalAssets} />
          <SummaryMetric
            label="Nhóm dịch vụ"
            value={Number(summary?.totalServices || 0).toLocaleString("vi-VN")}
          />
        </div>
      </div>

      <div className="grid min-h-[420px] grid-cols-1 gap-6 xl:grid-cols-12">
        <div className="xl:col-span-4">{chartContent}</div>

        <div className="min-h-[360px] min-w-0 xl:col-span-8">
          <CustomTable
            columns={tableColumns}
            data={rows}
            currentPage={1}
            setCurrentPage={() => {}}
            totalPages={1}
            setView={false}
            setEdit={false}
            setDelete={false}
            loading={loading}
            loadingText="Đang tải số liệu tài sản..."
          />
        </div>
      </div>
    </motion.section>
  );
};

const SummaryMetric = ({ label, value }) => (
  <div className="inline-flex min-h-[56px] items-center gap-3 rounded-[12px] border border-[#D2D5DA] bg-[#F8FAFC] px-4 py-3">
    <span className="whitespace-nowrap text-sm text-gray-600">{label}</span>
    <span className="font-sf-semibold text-base text-gray-900">{value}</span>
  </div>
);

const ChartSkeleton = () => (
  <div
    className="flex w-full flex-col items-center gap-4"
    aria-label="Đang tải biểu đồ"
  >
    <div className="size-[220px] rounded-full bg-slate-200 animate-pulse" />
    <div className="h-3 w-2/3 rounded bg-slate-200 animate-pulse" />
    <div className="h-3 w-1/2 rounded bg-slate-200 animate-pulse" />
  </div>
);

const RetryState = ({ error, onRetry }) => (
  <div className="flex flex-col items-center justify-center gap-3 text-center">
    <p className="text-sm text-red-600">{error}</p>
    <Button
      type="button"
      variant="primary"
      size="lg"
      onClick={onRetry}
    >
      Thử lại
    </Button>
  </div>
);

export default AssetReportSummary;
